import ProjRef from "@/shared/components/ProjRef";
import { useEffect, useState, useCallback } from "react";
import api, { fmtErr, API } from "@/shared/lib/api";
import {
  HardHat, ArrowLeft, ChevronRight, CheckCircle2, Trash2, Lock, Send,
} from "lucide-react";
import { toast } from "sonner";
import FileUpload from "@/shared/components/FileUpload";

const KIND_META = {
  wall:        { label: "Wall",        cls: "bg-sky-100 text-sky-800" },
  glass:       { label: "Glass",       cls: "bg-violet-100 text-violet-800" },
  others:      { label: "Other",       cls: "bg-slate-100 text-slate-700" },
  signage:     { label: "Signage",     cls: "bg-emerald-100 text-emerald-800" },
  interactive: { label: "Interactive", cls: "bg-rose-100 text-rose-800" },
};

const fullUrl = (u) => (!u ? "" : u.startsWith("http") ? u : `${API.replace(/\/api$/, "")}${u}`);

export default function SiteInspectorPortal() {
  const [projects, setProjects] = useState(null);
  const [code, setCode] = useState(null);

  const load = useCallback(() => {
    api.get("/site-inspector/board")
      .then((r) => setProjects(r.data.projects || []))
      .catch((e) => { toast.error(fmtErr(e.response?.data?.detail) || "Failed to load"); setProjects([]); });
  }, []);
  useEffect(() => { load(); }, [load]);

  const active = code ? (projects || []).find((p) => p.project_code === code) : null;

  return (
    <div className="p-5 sm:p-8 max-w-5xl mx-auto" data-testid="site-inspector-portal">
      <div className="flex items-center gap-3 mb-6">
        <div className="w-10 h-10 rounded-xl bg-lime-600 text-white flex items-center justify-center">
          <HardHat className="w-5 h-5" />
        </div>
        <div>
          <h1 className="text-xl font-bold text-zinc-900">Site Install</h1>
          <p className="text-sm text-zinc-500">Capture &amp; submit the final installed photo for each wall.</p>
        </div>
      </div>

      {projects === null ? (
        <div className="py-24 text-center text-zinc-400">Loading…</div>
      ) : active ? (
        <ProjectView project={active} onBack={() => setCode(null)} reload={load} />
      ) : projects.length === 0 ? (
        <div className="py-24 text-center text-zinc-400" data-testid="si-empty">
          No projects assigned to you yet. Ask your ISM to assign you as Site Inspector.
        </div>
      ) : (
        <div className="grid sm:grid-cols-2 gap-3" data-testid="si-project-list">
          {projects.map((p) => {
            const pct = p.total_walls ? Math.round((p.photos_done / p.total_walls) * 100) : 0;
            return (
              <button key={p.project_code} onClick={() => setCode(p.project_code)}
                data-testid={`si-project-${p.project_code}`}
                className="text-left rounded-2xl border border-zinc-200 hover:border-lime-400 hover:shadow-sm bg-white p-4 transition">
                <div className="flex items-center justify-between">
                  <span className="font-mono text-xs text-zinc-400"><ProjRef code={p.project_code} /></span>
                  <ChevronRight className="w-4 h-4 text-zinc-300" />
                </div>
                <div className="font-semibold text-zinc-900 mt-1 truncate">{p.name || "Untitled"}</div>
                <div className="text-xs text-zinc-500 truncate">{p.client_name || ""}</div>
                <div className="mt-3 flex items-center gap-2">
                  <div className="flex-1 h-2 rounded-full bg-zinc-100 overflow-hidden">
                    <div className="h-full bg-lime-500" style={{ width: `${pct}%` }} />
                  </div>
                  <span className="text-xs font-medium text-zinc-600">{p.photos_done}/{p.total_walls} done</span>
                </div>
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}

function ProjectView({ project, onBack, reload }) {
  const groups = project.groups || [];
  const [gkey, setGkey] = useState(groups[0]?.group || null);
  const active = groups.find((g) => g.group === gkey) || groups[0];

  return (
    <div data-testid="si-project-view">
      <button onClick={onBack} className="flex items-center gap-1.5 text-sm text-zinc-500 hover:text-zinc-900 mb-4" data-testid="si-back">
        <ArrowLeft className="w-4 h-4" /> All projects
      </button>
      <div className="mb-4">
        <span className="font-mono text-xs text-zinc-400"><ProjRef code={project.project_code} /></span>
        <h2 className="text-lg font-bold text-zinc-900">{project.name}</h2>
        <p className="text-sm text-zinc-500">{project.client_name}</p>
      </div>

      {/* sub-scope group tabs */}
      <div className="flex gap-2 flex-wrap mb-5 border-b border-zinc-200 pb-3" data-testid="si-group-tabs">
        {groups.map((g) => {
          const done = g.walls.filter((w) => w.locked).length;
          const total = g.walls.length;
          const isActive = g.group === active?.group;
          const allDone = done === total && total > 0;
          return (
            <button key={g.group} onClick={() => setGkey(g.group)}
              data-testid={`si-group-${g.group}`}
              className={`px-3 py-1.5 rounded-full text-sm font-medium transition flex items-center gap-1.5 ${
                isActive ? "bg-lime-600 text-white" : "bg-zinc-100 text-zinc-700 hover:bg-zinc-200"}`}>
              {g.group}
              <span className={`text-[11px] ${isActive ? "text-lime-100" : "text-zinc-400"}`}>{done}/{total}</span>
              {allDone && <CheckCircle2 className={`w-3.5 h-3.5 ${isActive ? "text-white" : "text-lime-600"}`} />}
            </button>
          );
        })}
      </div>

      {/* walls of the active group */}
      <div className="space-y-3" data-testid="si-walls">
        {(active?.walls || []).map((w) => (
          <WallCard key={w.boq_id} code={project.project_code} wall={w} reload={reload} />
        ))}
      </div>
    </div>
  );
}

function WallCard({ code, wall, reload }) {
  const [photos, setPhotos] = useState(wall.photos || []);
  const [locked, setLocked] = useState(!!wall.locked);
  const [busy, setBusy] = useState(false);
  const [uploading, setUploading] = useState(false);
  const km = KIND_META[wall.kind] || KIND_META.wall;

  async function onUploaded(url) {
    if (!url) return;
    setBusy(true);
    try {
      const { data } = await api.post(`/projects/${code}/boq/${wall.boq_id}/site-install-photo`, {
        file_url: url, file_name: url.split("/").pop(),
      });
      setPhotos((p) => [...p, data.photo]);
      toast.success("Photo added");
    } catch (e) {
      toast.error(fmtErr(e.response?.data?.detail) || "Upload failed");
    } finally { setBusy(false); }
  }

  async function remove(idx) {
    if (!window.confirm("Remove this photo?")) return;
    try {
      const { data } = await api.delete(`/projects/${code}/boq/${wall.boq_id}/site-install-photo/${idx}`);
      setPhotos(data.photos || []);
    } catch (e) { toast.error(fmtErr(e.response?.data?.detail) || "Failed to remove"); }
  }

  async function submit() {
    if (!photos.length) { toast.error("Upload at least one photo first."); return; }
    if (!window.confirm("Submit & lock this wall's installation? You won't be able to change the photos after.")) return;
    setBusy(true);
    try {
      await api.post(`/projects/${code}/boq/${wall.boq_id}/site-install-submit`);
      setLocked(true);
      toast.success("Installation submitted & locked");
      reload && reload();
    } catch (e) {
      toast.error(fmtErr(e.response?.data?.detail) || "Failed to submit");
    } finally { setBusy(false); }
  }

  return (
    <div className={`rounded-xl border bg-white p-3.5 ${locked ? "border-lime-400 bg-lime-50/30" : "border-zinc-200"}`}
         data-testid={`si-wall-${wall.boq_id}`}>
      <div className="flex items-center gap-2 flex-wrap">
        <span className={`text-[10px] font-mono uppercase tracking-wider px-1.5 py-0.5 rounded ${km.cls}`}>{km.label}</span>
        <span className="font-medium text-zinc-900 text-sm">{wall.label}</span>
        {locked ? (
          <span className="ml-auto flex items-center gap-1 text-xs font-semibold text-lime-700" data-testid={`si-locked-${wall.boq_id}`}>
            <Lock className="w-3.5 h-3.5" /> Installed &amp; submitted
          </span>
        ) : photos.length > 0 ? (
          <span className="ml-auto text-xs font-medium text-zinc-500">{photos.length} photo{photos.length !== 1 ? "s" : ""}</span>
        ) : (
          <span className="ml-auto text-xs text-zinc-400">Pending</span>
        )}
      </div>

      {photos.length > 0 && (
        <div className="flex gap-2 flex-wrap mt-3">
          {photos.map((ph, i) => (
            <div key={i} className="relative group">
              <a href={fullUrl(ph.url)} target="_blank" rel="noreferrer">
                <img src={fullUrl(ph.url)} alt="installed"
                     className="w-20 h-20 object-cover rounded-lg border border-zinc-200" />
              </a>
              {!locked && (
                <button onClick={() => remove(i)} data-testid={`si-remove-${wall.boq_id}-${i}`}
                  className="absolute -top-2 -right-2 w-6 h-6 rounded-full bg-rose-600 text-white flex items-center justify-center opacity-0 group-hover:opacity-100 transition">
                  <Trash2 className="w-3 h-3" />
                </button>
              )}
            </div>
          ))}
        </div>
      )}

      {!locked && (
        <div className="mt-3 flex flex-col sm:flex-row gap-2 sm:items-center">
          <div className="max-w-xs flex-1">
            <FileUpload
              label={busy ? "Working…" : (photos.length ? "Add another photo" : "Upload installed photo")}
              accept=".png,.jpg,.jpeg,.webp,.heic"
              onChange={onUploaded}
              onBusyChange={setUploading}
              testid={`si-upload-${wall.boq_id}`}
            />
          </div>
          <button onClick={submit} disabled={busy || uploading || !photos.length}
            data-testid={`si-submit-${wall.boq_id}`}
            className="h-11 px-4 rounded-lg bg-lime-600 hover:bg-lime-700 disabled:opacity-40 disabled:cursor-not-allowed text-white text-sm font-medium flex items-center justify-center gap-2 transition">
            <Send className="w-4 h-4" /> Submit &amp; lock
          </button>
        </div>
      )}
    </div>
  );
}
